/**
 * ==========================================
 * MAIN HEADER FIGMA - DEUXIÈME BARRE
 * ==========================================
 *
 * Composant basé exactement sur les spécifications Figma
 * - Logo BeFret à gauche
 * - Menus avec icônes: Home, Ship, Contact
 * - Boutons Track et Login à droite
 */

import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ProductionTranslationService } from '../../core/services/production-translation.service';
import { AuthService } from '../../core/services/auth.service';
import { UserProfile } from '../../core/models/user-profile';
import { GeolocationService } from '../../core/services/geolocation.service';
import { VirtualMailboxService } from '../../vm/services/virtual-mailbox.service';
import { Subscription } from 'rxjs';
import { environment } from '../../environment';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';

@Component({
    selector: 'app-main-header-figma',
    templateUrl: './main-header-figma.component.html',
    styleUrls: ['./main-header-figma.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, RouterModule, SharedModule]
})
export class MainHeaderFigmaComponent implements OnInit, OnDestroy {

  private languageSubscription?: Subscription;
  private profileSubscription?: Subscription;
  currentLanguage: string = 'fr';
  userProfile?: UserProfile | null = null;

  // Feature flag pour Virtual Mailbox (visible sur local/dev/acc, caché en prod)
  showVirtualMailbox = environment.featureFlags?.showVirtualMailbox ?? false;
  hasVmAccount = false;

  constructor(
    private router: Router,
    public translationService: ProductionTranslationService,
    public authService: AuthService,
    private geolocationService: GeolocationService,
    private vmService: VirtualMailboxService
  ) {}

  ngOnInit(): void {
    // Initialiser la langue courante
    this.currentLanguage = this.translationService.getCurrentLanguage();

    // S'abonner aux changements de langue pour mettre à jour l'interface
    this.languageSubscription = this.translationService.currentLanguage$.subscribe(
      (language) => {
        this.currentLanguage = language;
        console.log(`Main header updated for language: ${language}`);
      }
    );

    // S'abonner aux changements de profil utilisateur (BehaviorSubject)
    // Cela permet de mettre à jour le header quand le profil change (ex: après complete-profile)
    this.profileSubscription = this.authService.userProfile$.subscribe(profile => {
      if (profile) {
        this.userProfile = profile;
        console.log('📋 [MainHeader] Profile updated from BehaviorSubject:', profile.fullname || 'No fullname');
      }
    });

    // S'abonner aux changements d'état d'authentification
    this.authService.getUser().subscribe(user => {
      if (user) {
        this.authService.getUserProfile(user.uid).then(profile => {
          this.userProfile = profile;
          console.log('User profile loaded in main header:', this.userProfile);
        });
        // Vérifier si l'utilisateur a un compte VM actif
        if (this.showVirtualMailbox) {
          this.vmService.loadCurrentAccount().then(account => {
            this.hasVmAccount = !!(account && account.status === 'active');
          }).catch(() => {
            this.hasVmAccount = false;
          });
        }
      } else {
        this.userProfile = null;
        this.hasVmAccount = false;
        console.log('User logged out in main header');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }

  // ==========================================
  // NAVIGATION METHODS
  // ==========================================

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  navigateToShip(): void {
    this.router.navigate(['/new-shipment']);
  }

  navigateToContact(): void {
    this.router.navigate(['/contactus']);
  }

  navigateToTrack(): void {
    this.router.navigate(['/tracking']);
  }

  navigateToPriceCalculator(): void {
    // Détecter le pays automatiquement et naviguer
    this.geolocationService.detectLocation().subscribe({
      next: (result) => {
        if (result.success && result.data) {
          const countryCode = result.data.countryCode;
          this.router.navigate(['/price-calculator'], {
            queryParams: {
              location: countryCode,
              autoDetected: true
            }
          });
        } else {
          // Si la détection échoue, naviguer sans paramètres
          this.router.navigate(['/price-calculator']);
        }
      },
      error: () => {
        // En cas d'erreur, naviguer sans paramètres
        this.router.navigate(['/price-calculator']);
      }
    });
  }

  navigateToPickupPoints(): void {
    // Détecter le pays automatiquement et naviguer
    this.geolocationService.detectLocation().subscribe({
      next: (result) => {
        if (result.success && result.data) {
          const countryCode = result.data.countryCode;
          this.router.navigate(['/pickup-points'], {
            queryParams: {
              location: countryCode,
              autoDetected: true
            }
          });
        } else {
          // Si la détection échoue, naviguer sans paramètres
          this.router.navigate(['/pickup-points']);
        }
      },
      error: () => {
        // En cas d'erreur, naviguer sans paramètres
        this.router.navigate(['/pickup-points']);
      }
    });
  }

  navigateToLogin(): void {
    this.router.navigate(['/auth']);
  }

  navigateToBackofficeLogin(): void {
    this.router.navigate(['/backoffice-login']);
  }

  navigateToVirtualMailbox(): void {
    if (this.hasVmAccount) {
      this.router.navigate(['/portal/vm/dashboard']);
    } else {
      this.router.navigate(['/vm/info']);
    }
  }

  // Gestion de la déconnexion
  onLogout(): void {
    console.log('User logging out from main header...');
    this.authService.logout().then(() => {
      console.log('Logout successful');
      this.router.navigate(['/']);
    }).catch((error) => {
      console.error('Logout error:', error);
      this.router.navigate(['/']);
    });
  }

  // ==========================================
  // LANGUAGE METHODS (pour le menu mobile)
  // ==========================================

  setLanguage(language: string): void {
    this.translationService.changeLanguage(language).subscribe({
      next: () => {
        console.log(`Language changed to: ${language}`);
      },
      error: (error) => {
        console.error('Error changing language:', error);
      }
    });
  }
}